/**
 * Offline Status Indicator
 * Shows offline status and pending sync count
 */

import { Wifi, WifiOff, Cloud, CloudOff, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const OfflineIndicator = ({ 
  isOnline, 
  pendingCount = 0, 
  isSyncing = false,
  onSync,
  className = "" 
}) => {
  if (isOnline && pendingCount === 0 && !isSyncing) {
    return null; // Don't show anything when all is good
  }

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {/* Offline indicator */}
      {!isOnline && (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-amber-500/20 border border-amber-500/30">
              <WifiOff className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-xs text-amber-400 font-medium">Offline</span>
            </div>
          </TooltipTrigger>
          <TooltipContent className="bg-zinc-800 text-zinc-100 border-zinc-700">
            <p>Sin conexión a internet. Los datos se guardarán localmente.</p>
          </TooltipContent>
        </Tooltip>
      )}

      {/* Pending sync indicator */}
      {pendingCount > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={onSync}
              disabled={isSyncing || !isOnline}
              className="flex items-center gap-1.5 px-2 py-1 h-auto bg-blue-500/20 border border-blue-500/30 hover:bg-blue-500/30"
            >
              {isSyncing ? (
                <Loader2 className="w-3.5 h-3.5 text-blue-400 animate-spin" />
              ) : (
                <Cloud className="w-3.5 h-3.5 text-blue-400" />
              )}
              <span className="text-xs text-blue-400 font-medium">
                {pendingCount} pendiente{pendingCount > 1 ? 's' : ''}
              </span>
              {isOnline && !isSyncing && (
                <RefreshCw className="w-3 h-3 text-blue-400" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent className="bg-zinc-800 text-zinc-100 border-zinc-700">
            <p>
              {isSyncing 
                ? 'Sincronizando...' 
                : isOnline 
                  ? 'Click para sincronizar ahora' 
                  : 'Se sincronizará al recuperar conexión'}
            </p>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};

export default OfflineIndicator;
